import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api';
import {
  PieChart, Pie, Cell, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

const COLORS = ['#3b82f6', '#f59e0b', '#8b5cf6', '#10b981', '#ef4444', '#ec4899'];

// Exchange rates (hardcoded for demo – replace with real API later)
const RATES = { BTC: 63035, ETH: 1770, USDT: 1 };

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [assets, setAssets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [activeCases, setActiveCases] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    // ✅ cache‑buster ensures fresh balance after admin override
    api.get(`/api/user/profile?_=${Date.now()}`).then(res => setUser(res.data));
    api.get('/api/assets/my-assets').then(res => setAssets(res.data));
    api.get('/api/assets/transactions').then(res => setTransactions(res.data));

    api.get('/api/releases').then(res => {
      const releases = res.data || [];
      const active = releases.filter(
        r => r.status !== 'final_approved' && r.status !== 'rejected'
      ).length;
      setActiveCases(active);
    });
  }, [navigate]);

  // ---------- Read per‑currency balances directly from user object ----------
  const balanceBTC = user?.balanceBTC ?? 0;
  const balanceETH = user?.balanceETH ?? 0;
  const balanceUSDT = user?.balanceUSDT ?? 0;

  // Total dollar value using the per‑currency balances
  const totalUSD = balanceBTC * RATES.BTC + balanceETH * RATES.ETH + balanceUSDT * RATES.USDT;

  const pendingAssets = assets.filter(
    a => a.status !== 'asset_transfer' && a.status !== 'rejected'
  ).length;
  const releasedAssets = assets.filter(a => a.status === 'asset_transfer').length;

  const statusCounts = {
    received: 0, security_screening: 0, ownership_verification: 0,
    vault_storage: 0, release_approval: 0, asset_transfer: 0,
  };
  assets.forEach(a => {
    if (statusCounts[a.status] !== undefined) statusCounts[a.status]++;
  });
  const pieData = Object.entries(statusCounts).map(([name, value]) => ({ name, value }));

  // Area chart – use totalUSD for the last month
  const areaData = [
    { month: 'Jan', balance: totalUSD * 0.3 },
    { month: 'Feb', balance: totalUSD * 0.4 },
    { month: 'Mar', balance: totalUSD * 0.6 },
    { month: 'Apr', balance: totalUSD * 0.7 },
    { month: 'May', balance: totalUSD * 0.85 },
    { month: 'Jun', balance: totalUSD },
  ];

  const copyAddress = () => {
    navigator.clipboard.writeText(user?.walletAddress);
    alert('Copied!');
  };

  return (
    <div>
      <h2>Welcome back, {user?.name}</h2>
      <p className="last-login" style={{ color: '#64748b', marginBottom: '1rem' }}>
        Last login: {new Date().toLocaleString()}
      </p>
      <div className="wallet-box">
        <span>Wallet: <code>{user?.walletAddress}</code></span>
        <button className="copy-btn" onClick={copyAddress}>Copy</button>
      </div>

      <div className="stat-cards" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="card">
          <h3>TOTAL VAULT BALANCE</h3>
          <p>${totalUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
            BTC: {balanceBTC}  ETH: {balanceETH}  USDT: {balanceUSDT}
          </span>
        </div>
        <div className="card">
          <h3>Pending Assets</h3>
          <p>{pendingAssets}</p>
          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{pendingAssets} items</span>
        </div>
        <div className="card">
          <h3>Released Assets</h3>
          <p>{releasedAssets}</p>
          <span style={{ fontSize: '0.85rem', color: '#64748b' }}>{releasedAssets} items</span>
        </div>
        <div className="card">
          <h3>Active Cases</h3>
          <p>{activeCases}</p>
          <Link to="/cases" style={{ fontSize: '0.85rem', color: '#3b82f6' }}>View All →</Link>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3>Send Assets</h3>
          <p style={{ color: '#475569' }}>Send cryptocurrency to an external wallet.</p>
        </div>
        <Link to="/send" className="btn-primary" style={{ padding: '0.75rem 1.5rem', borderRadius: '0.5rem', background: '#3b82f6', color: 'white', fontWeight: 600 }}>
          Send Now
        </Link>
      </div>

      <div className="stat-cards" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '1.5rem' }}>
        <Link to="/verification" className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
          <span style={{ fontSize: '2rem' }}>🆔</span>
          <h3 style={{ marginTop: '0.5rem' }}>Verification Center</h3>
          <p style={{ fontSize: '0.9rem', color: '#475569' }}>KYC & Documents</p>
        </Link>
        <Link to="/security-center" className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
          <span style={{ fontSize: '2rem' }}>🛡️</span>
          <h3 style={{ marginTop: '0.5rem' }}>Security Center</h3>
          <p style={{ fontSize: '0.9rem', color: '#475569' }}>2FA & Settings</p>
        </Link>
        <Link to="/support" className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
          <span style={{ fontSize: '2rem' }}>🎫</span>
          <h3 style={{ marginTop: '0.5rem' }}>Support</h3>
          <p style={{ fontSize: '0.9rem', color: '#475569' }}>Help & Tickets</p>
        </Link>
        <Link to="/releases" className="card" style={{ textAlign: 'center', padding: '1.5rem' }}>
          <span style={{ fontSize: '2rem' }}>📤</span>
          <h3 style={{ marginTop: '0.5rem' }}>Release Management</h3>
          <p style={{ fontSize: '0.9rem', color: '#475569' }}>Approve & Track</p>
        </Link>
      </div>

      <div className="charts-row">
        <div className="chart-box">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Vault Balance Overview</h3>
            <Link to="/vault-overview" style={{ fontSize: '0.85rem', color: '#3b82f6' }}>View All →</Link>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={areaData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="balance" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="chart-box">
          <h3>Recovery Progress</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {pieData.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="pie-legend">
            {pieData.map((entry, index) => (
              <span key={index} style={{ color: COLORS[index] }}>{entry.name}: {entry.value}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="tables-row">
        <div className="table-box">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Recent Activities</h3>
            <Link to="/transactions" style={{ fontSize: '0.85rem', color: '#3b82f6' }}>View All →</Link>
          </div>
          <table>
            <thead><tr><th>Type</th><th>Amount</th><th>Currency</th><th>Date</th></tr></thead>
            <tbody>
              {transactions.slice(0, 5).map(tx => (
                <tr key={tx.id}>
                  <td>{tx.type}</td>
                  <td>{tx.amount}</td>
                  <td>{tx.currency || 'ETH'}</td>
                  <td>{new Date(tx.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="table-box">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3>Security Status</h3>
            <Link to="/security-center" style={{ fontSize: '0.85rem', color: '#3b82f6' }}>View All →</Link>
          </div>
          <table>
            <thead><tr><th>Feature</th><th>Status</th></tr></thead>
            <tbody>
              <tr><td>Two-Factor Auth</td><td>{user?.twoFactorEnabled ? '✅ Enabled' : '❌ Disabled'}</td></tr>
              <tr><td>Biometric Login</td><td>⚠️ Not Set</td></tr>
              <tr><td>Device Verification</td><td>✅ Verified</td></tr>
              <tr><td>Security Score</td><td>{user?.securityScore || 0}/100</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;